import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Button, Input, Select, formatSelectOptions } from '../../../components'
import { storyService } from '../services'
import { appName } from '../appConfig'
import { getStoryRole } from '../../../helpers'
import '../index.css'

// Talare rows only carry a single free-text "contact" field (no separate
// email/phone) — used as a best-effort email since the placeholder/convention
// is namn@företag.se. Deltagare rows have real email/phone fields.
const buildParticipantIndex = (stories) => {
  const byKey = new Map()
  stories.forEach(s => {
    const d = s.story_data || {}
    const rows = [
      ...(d.speakers || []).filter(p => p && p.name).map(p => ({ ...p, role: 'speaker' })),
      ...(d.participants || []).filter(p => p && p.name).map(p => ({ ...p, role: 'participant' })),
    ]
    rows.forEach(p => {
      // Dedup by person_uuid where available (correct across name/spelling
      // variants once a person is picker-backed, and across a speaker/
      // deltagare appearing under both roles) — falls back to the legacy
      // name+company string key for any row not yet migrated by
      // backfillStoryPersons.js.
      const key = p.person_uuid || `${p.name}__${p.company || ''}`
      if (!byKey.has(key)) byKey.set(key, { name: p.name, person_uuid: null, company: p.company || '', email: '', phone: '', stories: [] })
      const entry = byKey.get(key)
      const email = p.email || (p.role === 'speaker' ? p.contact : '')
      if (!entry.person_uuid && p.person_uuid) entry.person_uuid = p.person_uuid
      if (!entry.email && email)   entry.email = email
      if (!entry.phone && p.phone) entry.phone = p.phone
      entry.stories.push({
        slug:    s.story_slug,
        topic:   d.topic || s.story_slug,
        hasHtml: s.has_html,
        role:    p.role,
      })
    })
  })
  return [...byKey.values()].sort((a, b) => a.name.localeCompare(b.name))
}

const EMPTY_FILTERS = { search: '', company: '', story: '', role: '' }

const applyParticipantFilters = (participants, filters) => {
  const search = filters.search.trim().toLowerCase()
  return participants.filter(p => {
    if (search && !p.name.toLowerCase().includes(search)) return false
    if (filters.company && p.company !== filters.company) return false
    if (filters.story && !p.stories.some(story => story.slug === filters.story)) return false
    if (filters.role && !p.stories.some(story => story.role === filters.role)) return false
    return true
  })
}

const deriveCompanyOptions = (participants) => [...new Set(
  participants.map(p => p.company).filter(Boolean)
)].sort((a, b) => a.localeCompare(b))

const deriveStoryOptions = (participants) => {
  const bySlug = new Map()
  participants.forEach(p => p.stories.forEach(s => { if (!bySlug.has(s.slug)) bySlug.set(s.slug, s.topic) }))
  return [...bySlug.entries()]
    .map(([slug, topic]) => ({ slug, topic }))
    .sort((a, b) => a.topic.localeCompare(b.topic))
}

const SORT_COLUMNS = [
  { key: 'name',    label: 'Namn' },
  { key: 'company', label: 'Företag' },
  { key: 'email',   label: 'E-post' },
  { key: 'phone',   label: 'Telefon' },
  { key: 'count',   label: 'Antal', align: 'center' },
]

const sortParticipants = (participants, sort) => {
  const sorted = [...participants].sort((a, b) => {
    let cmp
    if (sort.key === 'count') cmp = a.stories.length - b.stories.length
    else                      cmp = (a[sort.key] || '').localeCompare(b[sort.key] || '')
    if (cmp === 0) cmp = a.name.localeCompare(b.name) // stable tiebreak on name
    return sort.dir === 'desc' ? -cmp : cmp
  })
  return sorted
}

const Participants = () => {
  const navigate = useNavigate()
  const [stories, setStories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState(null)
  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const [sort, setSort]       = useState({ key: 'name', dir: 'asc' })

  const updateFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }))

  // Adapter for the shared Input/Select onChange duality (see StoryList.js).
  const handleFilterChange = (event) => {
    const { name, value } = event.target !== undefined ? event.target : event
    updateFilter(name, value || '')
  }

  const toggleSort = (key) => setSort(prev =>
    prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
  )

  useEffect(() => {
    const ctrl = new AbortController()
    storyService.getAll(ctrl.signal)
      .then(res => { setStories(res.stories || []); setLoading(false) })
      .catch(err => { if (err.name !== 'AbortError') { setError('Kunde inte ladda stories'); setLoading(false) } })
    return () => ctrl.abort()
  }, [])

  const handleStoryClick = (story) => {
    if (story.hasHtml) navigate(`/${appName}/${story.slug}`)
    else                navigate(`/${appName}/${story.slug}/edit`)
  }

  if (loading) return <div className="ks-page"><p style={{ color: 'var(--color-dark-grey)' }}>Laddar…</p></div>
  if (error)   return <div className="ks-page"><p style={{ color: 'var(--color-intense-red)' }}>{error}</p></div>

  const allParticipants   = buildParticipantIndex(stories)
  const companyOptions    = deriveCompanyOptions(allParticipants)
  const storyOptions      = deriveStoryOptions(allParticipants)
  const hasActiveFilters  = Object.values(filters).some(Boolean)
  const participants      = sortParticipants(applyParticipantFilters(allParticipants, filters), sort)

  return (
    <div className="ks-page">
      {allParticipants.length > 0 && (
        <div className="ks-filter-bar">
          <Input
            className="ks-filter-search"
            type="text"
            name="search"
            placeholder="Sök på namn..."
            value={filters.search}
            onChange={handleFilterChange}
            isNullable
          />
          <Select
            className="ks-filter-select"
            name="company"
            value={filters.company}
            placeholder="Företag"
            options={formatSelectOptions({ options: companyOptions.map(name => ({ name })), optionValue: 'name', optionLabel: 'name' })}
            onChange={handleFilterChange}
            isNullable
          />
          <Select
            className="ks-filter-select"
            name="story"
            value={filters.story}
            placeholder="Story"
            options={formatSelectOptions({ options: storyOptions, optionValue: 'slug', optionLabel: 'topic' })}
            onChange={handleFilterChange}
            isNullable
          />
          <Select
            className="ks-filter-select"
            name="role"
            value={filters.role}
            placeholder="Roll"
            options={[
              { value: 'speaker', label: getStoryRole('speaker').label },
              { value: 'participant', label: getStoryRole('participant').label },
            ]}
            onChange={handleFilterChange}
            isNullable
          />
          {hasActiveFilters && (
            <Button value="Rensa filter" className="button ks-filter-clear" onClick={() => setFilters(EMPTY_FILTERS)} />
          )}
        </div>
      )}

      {allParticipants.length === 0 ? (
        <p style={{ color: 'var(--color-dark-grey)', fontStyle: 'italic' }}>Inga talare eller deltagare registrerade ännu.</p>
      ) : participants.length === 0 ? (
        <p className="ks-no-matches">Inga deltagare matchar filtren.</p>
      ) : (
        <div className="ks-table-scroll">
        <table className="ks-table">
          <colgroup>
            <col style={{ width: '16%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '17%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '7%' }} />
            <col style={{ width: '31%' }} />
          </colgroup>
          <thead>
            <tr>
              {SORT_COLUMNS.map(col => (
                <th key={col.key} className="ks-sortable" style={col.align ? { textAlign: col.align } : undefined} onClick={() => toggleSort(col.key)}>
                  {col.label}
                  <span className={`ks-sort-arrow${sort.key === col.key ? ' active' : ''}`}>
                    {sort.key === col.key ? (sort.dir === 'asc' ? '▲' : '▼') : '▲'}
                  </span>
                </th>
              ))}
              <th>Stories</th>
            </tr>
          </thead>
          <tbody>
            {participants.map((p, i) => (
              <tr key={i}>
                <td style={{ fontFamily: 'Rubik-Medium' }}>
                  {p.person_uuid ? <Link className="link" to={`/network/persons/${p.person_uuid}`}>{p.name}</Link> : p.name}
                </td>
                <td style={{ color: 'var(--color-dark-grey)', fontSize: '0.85em' }}>{p.company || '—'}</td>
                <td style={{ color: 'var(--color-dark-grey)', fontSize: '0.85em' }}>
                  {p.email ? <a href={`mailto:${p.email}`}>{p.email}</a> : '—'}
                </td>
                <td style={{ color: 'var(--color-dark-grey)', fontSize: '0.85em' }}>
                  {p.phone ? <a href={`tel:${p.phone}`}>{p.phone}</a> : '—'}
                </td>
                <td style={{ color: 'var(--color-dark-grey)', fontSize: '0.85em', textAlign: 'center' }}>{p.stories.length}</td>
                <td>
                  <div className="ks-story-list">
                    {p.stories.map((story, si) => {
                      const role = getStoryRole(story.role)
                      return (
                        <span key={si} className="ks-story-item" onClick={() => handleStoryClick(story)}>
                          <span className="ks-story-topic">{story.topic}</span>
                          <span className={`${role.pillClassName} ks-story-status-pill`}>{role.label}</span>
                        </span>
                      )
                    })}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  )
}

export default Participants
